import { StoreState } from '@/core/models/store.interface';
import { CancelToken } from 'axios';
import { Module } from 'vuex';
import { ResumeDuplicatePayload, ResumeFileReviewData, ResumeUploadBatch, ResumeUploadManifestFile } from './resume-upload.interface';
import { ResumeUploadService } from './resume-upload.service';

const service = new ResumeUploadService();

const PROCESS_BATCH_PAGE_SIZE = 100;
const PROCESS_BATCH_PAGES_SCANNED = 2;
const PROCESS_BATCH_LIMIT = 20;

export interface ResumeUploadState {
   processBatches: Array<ResumeUploadBatch>;
}

const ResumeUploadStore: Module<ResumeUploadState, StoreState> = {
   namespaced: true,
   state: {
      processBatches: [],
   },
   getters: {},
   mutations: {
      SET_PROCESS_BATCHES(state, batches: Array<ResumeUploadBatch>) {
         state.processBatches = batches;
      },
      SET_PROCESS_BATCH(state, batch: ResumeUploadBatch) {
         const batchIndex = state.processBatches.findIndex((current) => current.id === batch.id);
         const existing = batchIndex > -1 ? state.processBatches[batchIndex] : undefined;
         const merged: ResumeUploadBatch = {
            ...existing,
            ...batch,
            counts: batch.counts ?? existing?.counts,
            files: batch.files ?? existing?.files,
            call_list: batch.call_list ?? existing?.call_list,
            resume_batch_status: batch.resume_batch_status ?? existing?.resume_batch_status,
         };
         if (batchIndex > -1) state.processBatches.splice(batchIndex, 1, merged);
         else state.processBatches.unshift(merged);
      },
   },
   actions: {
      async getFileStatuses(context, payload: { cancelToken?: CancelToken } = {}) {
         const response = await service.getFileStatuses(payload.cancelToken);
         return response;
      },
      async createBatch(context, payload: { files: Array<ResumeUploadManifestFile>; cancelToken?: CancelToken }) {
         const response = await service.createBatch(payload.files, payload.cancelToken);
         return response;
      },
      async getBatches(context, payload: { page?: number; perPage?: number; cancelToken?: CancelToken } = {}) {
         const response = await service.getBatches({ page: payload.page, perPage: payload.perPage }, payload.cancelToken);
         return response;
      },
      async loadProcessBatches(context) {
         const firstPage = await service.getBatches({ page: 1, perPage: PROCESS_BATCH_PAGE_SIZE });
         const lastPageNumber: number = firstPage.meta?.last_page ?? 1;
         const scanned: Array<ResumeUploadBatch> = [];

         for (let pageNumber = lastPageNumber; pageNumber > Math.max(0, lastPageNumber - PROCESS_BATCH_PAGES_SCANNED); pageNumber--) {
            const pageResponse =
               pageNumber === 1 ? firstPage : await service.getBatches({ page: pageNumber, perPage: PROCESS_BATCH_PAGE_SIZE });
            scanned.push(...(pageResponse.data ?? []));
         }

         const batches = scanned
            .filter((batch) => (batch.file_count ?? 0) > 0)
            .sort((left, right) => right.id - left.id)
            .slice(0, PROCESS_BATCH_LIMIT);

         context.commit('SET_PROCESS_BATCHES', batches);
      },
      async getBatch(context, payload: { batchId: number; cancelToken?: CancelToken }) {
         const response = await service.getBatch(payload.batchId, payload.cancelToken);
         return response;
      },
      async getFile(
         context,
         payload: {
            batchId: number;
            fileId: number;
            appendReviewUrl?: boolean;
            appendResumeUrl?: boolean;
            cancelToken?: CancelToken;
         },
      ) {
         const response = await service.getFile(
            payload.batchId,
            payload.fileId,
            { appendReviewUrl: payload.appendReviewUrl, appendResumeUrl: payload.appendResumeUrl },
            payload.cancelToken,
         );
         return response;
      },
      async cancelBatch(context, payload: { batchId: number; cancelToken?: CancelToken }) {
         const response = await service.cancelBatch(payload.batchId, payload.cancelToken);
         context.commit('SET_PROCESS_BATCH', response.data);
         return response;
      },
      async getParsedResume(context, payload: { identifier: string; cancelToken?: CancelToken }) {
         const response = await service.getParsedResume(payload.identifier, payload.cancelToken);
         return response;
      },
      async updateBatch(
         context,
         payload: { batchId: number; external_source_id?: number | null; call_list_id?: number | null; cancelToken?: CancelToken },
      ) {
         const response = await service.updateBatch(
            payload.batchId,
            { external_source_id: payload.external_source_id, call_list_id: payload.call_list_id },
            payload.cancelToken,
         );
         return response;
      },
      async addFilesToBatch(context, payload: { batchId: number; files: Array<ResumeUploadManifestFile>; cancelToken?: CancelToken }) {
         const response = await service.addFilesToBatch(payload.batchId, payload.files, payload.cancelToken);
         return response;
      },
      async confirmBatch(context, payload: { batchId: number; cancelToken?: CancelToken }) {
         const response = await service.confirmBatch(payload.batchId, payload.cancelToken);
         return response;
      },
      async deleteFile(context, payload: { batchId: number; fileId: number; cancelToken?: CancelToken }) {
         await service.deleteFile(payload.batchId, payload.fileId, payload.cancelToken);
      },
      async deleteFiles(context, payload: { batchId: number; fileIds: Array<number>; cancelToken?: CancelToken }) {
         const response = await service.deleteFiles(payload.batchId, payload.fileIds, payload.cancelToken);
         return response;
      },
      async uploadFile(
         context,
         payload: {
            presignedUrl: string;
            file: File;
            headers?: Record<string, string | Array<string>>;
            cancelToken?: CancelToken;
            onProgress?: (loaded: number, total: number) => void;
         },
      ) {
         await service.uploadToS3(payload.presignedUrl, payload.file, {
            headers: payload.headers,
            cancelToken: payload.cancelToken,
            onProgress: payload.onProgress,
         });
      },
      async updateFileReviewData(
         context,
         payload: { batchId: number; fileId: number; data: Partial<ResumeFileReviewData>; cancelToken?: CancelToken },
      ) {
         const response = await service.updateFileReviewData(payload.batchId, payload.fileId, payload.data, payload.cancelToken);
         return response;
      },
      async reviewFiles(context, payload: { batchId: number; fileIds: Array<number>; cancelToken?: CancelToken }) {
         const response = await service.reviewFiles(payload.batchId, payload.fileIds, payload.cancelToken);
         return response;
      },
      async readyForReviewFiles(context, payload: { batchId: number; fileIds: Array<number>; cancelToken?: CancelToken }) {
         const response = await service.readyForReviewFiles(payload.batchId, payload.fileIds, payload.cancelToken);
         return response;
      },
      async validateDuplicate(
         context,
         payload: { batchId: number; fileId: number; resolution: ResumeDuplicatePayload; cancelToken?: CancelToken },
      ) {
         const response = await service.validateDuplicate(payload.batchId, payload.fileId, payload.resolution, payload.cancelToken);
         return response;
      },
      async getSimilarCandidates(
         context,
         payload: { batchId: number; fileId: number; page?: number; perPage?: number; cancelToken?: CancelToken },
      ) {
         const response = await service.getSimilarCandidates(
            payload.batchId,
            payload.fileId,
            { page: payload.page, per_page: payload.perPage },
            payload.cancelToken,
         );
         return response;
      },
   },
};

export default ResumeUploadStore;
